import React, { useState, useEffect } from 'react';
import { Users, FolderOpen, Plus, X, CheckCircle, Clock, Star, ChevronDown } from 'lucide-react';
import api from '../lib/api';
import { LoadingPage, Badge, Spinner } from '../components/shared';
import toast from 'react-hot-toast';

export default function AdminJudgingPage() {
  const [tab, setTab] = useState('projects');

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Judging Management</h1>
          <p className="page-subtitle">Create judge accounts, assign projects, and monitor scoring progress</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 28, borderBottom: '1px solid rgba(255,255,255,0.07)', paddingBottom: 0 }}>
        {[
          { key: 'projects', label: 'Projects & Assignments', icon: FolderOpen },
          { key: 'judges',   label: 'Judges',                 icon: Users },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 18px', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '0.875rem', fontWeight: tab === t.key ? 600 : 400,
              color: tab === t.key ? 'var(--gold)' : 'var(--text-muted)',
              borderBottom: tab === t.key ? '2px solid var(--gold)' : '2px solid transparent',
              marginBottom: -1, transition: 'all 0.15s',
            }}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'projects' && <ProjectsTab />}
      {tab === 'judges'   && <JudgesTab />}
    </div>
  );
}

// ── Projects Tab ──────────────────────────────────────────────────────────────
function ProjectsTab() {
  const [projects, setProjects] = useState([]);
  const [judges, setJudges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState({});
  const [removing, setRemoving] = useState({});

  const load = async () => {
    try {
      const [projRes, judgeRes] = await Promise.all([
        api.get('/admin/judges/assignments'),
        api.get('/admin/judges'),
      ]);
      setProjects(projRes.data);
      setJudges(judgeRes.data.filter(j => j.status === 'approved'));
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleAssign = async (projectId, judgeId) => {
    if (!judgeId) return;
    setAssigning(a => ({ ...a, [projectId]: true }));
    try {
      await api.post('/admin/judges/assign', { judge_id: judgeId, project_ids: [projectId] });
      toast.success('Judge assigned');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to assign');
    } finally {
      setAssigning(a => ({ ...a, [projectId]: false }));
    }
  };

  const handleRemove = async (projectId, judgeId) => {
    const key = `${projectId}-${judgeId}`;
    setRemoving(r => ({ ...r, [key]: true }));
    try {
      await api.delete('/admin/judges/assign', { data: { judge_id: judgeId, project_id: projectId } });
      toast.success('Assignment removed');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove');
    } finally {
      setRemoving(r => ({ ...r, [key]: false }));
    }
  };

  if (loading) return <LoadingPage />;

  const unassigned = projects.filter(p => p.judges.length === 0);
  const assigned   = projects.filter(p => p.judges.length > 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {projects.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '60px 40px', color: 'var(--text-muted)' }}>
          <FolderOpen size={40} style={{ opacity: 0.3, margin: '0 auto 16px', display: 'block' }} />
          <p>No projects available for judging yet.</p>
        </div>
      )}

      {unassigned.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.08em', color: 'var(--error)', textTransform: 'uppercase', marginBottom: 12 }}>
            Unassigned ({unassigned.length})
          </div>
          {unassigned.map(p => (
            <ProjectRow key={p.project_id} project={p} judges={judges}
              onAssign={handleAssign} onRemove={handleRemove}
              assigning={assigning[p.project_id]} removing={removing} />
          ))}
        </div>
      )}

      {assigned.length > 0 && (
        <div>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.08em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 12 }}>
            Assigned ({assigned.length})
          </div>
          {assigned.map(p => (
            <ProjectRow key={p.project_id} project={p} judges={judges}
              onAssign={handleAssign} onRemove={handleRemove}
              assigning={assigning[p.project_id]} removing={removing} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectRow({ project, judges, onAssign, onRemove, assigning, removing }) {
  const [selectedJudge, setSelectedJudge] = useState('');
  const assignedIds = project.judges.map(j => j.judge_id);
  const availableJudges = judges.filter(j => !assignedIds.includes(j.id));
  const allScored = project.judges.length > 0 && project.judges.every(j => j.scored);

  return (
    <div style={{
      padding: '16px 20px', marginBottom: 10,
      background: 'var(--ink-mid)',
      border: `1px solid ${allScored ? 'rgba(34,197,94,0.2)' : project.judges.length > 0 ? 'rgba(255,255,255,0.07)' : 'rgba(239,68,68,0.2)'}`,
      borderRadius: 'var(--radius-lg)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        {/* Project info */}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 500, marginBottom: 3 }}>{project.topic_title}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
            {project.student_name} · <span style={{ color: 'var(--teal)' }}>{project.domain || 'General'}</span>
            {project.final_score && (
              <span style={{ marginLeft: 12, color: 'var(--gold)', fontFamily: 'var(--font-mono)' }}>
                Avg: {parseFloat(project.final_score).toFixed(2)}/10
              </span>
            )}
          </div>

          {/* Assigned judges */}
          {project.judges.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
              {project.judges.map(j => {
                const key = `${project.project_id}-${j.judge_id}`;
                return (
                  <div key={j.judge_id} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '4px 10px', borderRadius: 999, fontSize: '0.75rem',
                    background: j.scored ? 'rgba(34,197,94,0.1)' : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${j.scored ? 'rgba(34,197,94,0.3)' : 'rgba(255,255,255,0.1)'}`,
                    color: j.scored ? 'var(--success)' : 'var(--text-secondary)',
                  }}>
                    {j.scored
                      ? <CheckCircle size={11} />
                      : <Clock size={11} />
                    }
                    {j.judge_name}
                    {j.scored && j.score && (
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.7rem', color: 'var(--gold)', marginLeft: 2 }}>
                        {parseFloat(j.score).toFixed(1)}
                      </span>
                    )}
                    <button
                      onClick={() => onRemove(project.project_id, j.judge_id)}
                      disabled={removing[key]}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0 0 0 2px', display: 'flex', alignItems: 'center' }}
                    >
                      {removing[key] ? <Spinner size={10} /> : <X size={11} />}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Assign judge */}
        {availableJudges.length > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
            <select
              className="form-input"
              style={{ fontSize: '0.8rem', padding: '6px 10px', minWidth: 160 }}
              value={selectedJudge}
              onChange={e => setSelectedJudge(e.target.value)}
            >
              <option value="">Assign a judge…</option>
              {availableJudges.map(j => (
                <option key={j.id} value={j.id}>
                  {j.first_name} {j.last_name}
                  {j.expertise?.length ? ` (${j.expertise.slice(0,2).join(', ')})` : ''}
                </option>
              ))}
            </select>
            <button
              className="btn btn-primary btn-sm"
              disabled={!selectedJudge || assigning}
              onClick={() => { onAssign(project.project_id, selectedJudge); setSelectedJudge(''); }}
            >
              {assigning ? <Spinner size={14} /> : 'Assign'}
            </button>
          </div>
        )}
        {availableJudges.length === 0 && judges.length > 0 && (
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', flexShrink: 0 }}>All judges assigned</span>
        )}
        {judges.length === 0 && (
          <span style={{ fontSize: '0.75rem', color: 'var(--error)', flexShrink: 0 }}>No judges created yet</span>
        )}
      </div>
    </div>
  );
}

// ── Judges Tab ─────────────────────────────────────────────────────────────────
function JudgesTab() {
  const [judges, setJudges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = () => {
    api.get('/admin/judges')
      .then(r => setJudges(r.data))
      .catch(() => toast.error('Failed to load judges'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) return <LoadingPage />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <Plus size={15} /> Add Judge
        </button>
      </div>

      {judges.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px 40px', color: 'var(--text-muted)' }}>
          <Users size={40} style={{ opacity: 0.3, margin: '0 auto 16px', display: 'block' }} />
          <p style={{ marginBottom: 16 }}>No judges added yet.</p>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={15} /> Add First Judge
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {judges.map(j => (
            <div key={j.id} className="card" style={{ padding: '16px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: 3 }}>
                    {j.first_name} {j.last_name}
                    {j.status !== 'approved' && (
                      <span style={{ marginLeft: 8, fontSize: '0.72rem', color: 'var(--error)' }}>({j.status})</span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 6 }}>
                    {j.email}
                    {j.affiliation && <span style={{ marginLeft: 12 }}>· {j.affiliation}</span>}
                  </div>
                  {j.expertise?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {j.expertise.map(ex => (
                        <span key={ex} style={{
                          padding: '2px 8px', borderRadius: 999, fontSize: '0.72rem',
                          background: 'rgba(42,157,143,0.1)', border: '1px solid rgba(42,157,143,0.25)',
                          color: 'var(--teal)',
                        }}>{ex}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ display: 'flex', gap: 16 }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.2rem', color: 'var(--gold)', fontWeight: 600 }}>
                        {j.assigned_count || 0}
                      </div>
                      <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Assigned</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.2rem', color: 'var(--success)', fontWeight: 600 }}>
                        {j.scored_count || 0}
                      </div>
                      <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Scored</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && <CreateJudgeModal onClose={() => setShowCreate(false)} onCreated={load} />}
    </div>
  );
}

// ── Create Judge Modal ─────────────────────────────────────────────────────────
function CreateJudgeModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', affiliation: '', bio: '',
  });
  const [expertiseInput, setExpertiseInput] = useState('');
  const [expertise, setExpertise] = useState([]);
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(null);

  const addExpertise = () => {
    const tag = expertiseInput.trim();
    if (tag && !expertise.includes(tag)) {
      setExpertise(e => [...e, tag]);
    }
    setExpertiseInput('');
  };

  const handleSubmit = async () => {
    if (!form.first_name || !form.last_name || !form.email) {
      toast.error('First name, last name and email are required.');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/admin/judges/create', { ...form, expertise });
      setCreated(res.data);
      onCreated();
      toast.success('Judge account created and welcome email sent.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create judge.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{
        background: 'var(--ink-mid)', border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 16, padding: 32, maxWidth: 520, width: '100%', maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: 500 }}>Add Judge</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
            <X size={20} />
          </button>
        </div>

        {created ? (
          <div style={{ textAlign: 'center' }}>
            <CheckCircle size={48} style={{ color: 'var(--success)', margin: '0 auto 16px', display: 'block' }} />
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', marginBottom: 8 }}>Judge account created</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: 20 }}>
              A welcome email with login credentials has been sent to <strong>{created.user?.email}</strong>.
            </p>
            <div style={{ padding: '16px', background: 'rgba(255,255,255,0.03)', borderRadius: 10, marginBottom: 20, fontSize: '0.85rem' }}>
              <div style={{ marginBottom: 8 }}>Temporary password:</div>
              <code style={{ color: 'var(--gold)', fontSize: '1rem', letterSpacing: '0.1em' }}>{created.tempPassword}</code>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 6 }}>
                Share this with the judge if the email doesn't arrive. They should change it on first login.
              </div>
            </div>
            <button className="btn btn-primary" onClick={onClose} style={{ width: '100%' }}>Done</button>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div className="form-group">
                <label className="form-label">First Name *</label>
                <input className="form-input" value={form.first_name}
                  onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} placeholder="First name" />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name *</label>
                <input className="form-input" value={form.last_name}
                  onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} placeholder="Last name" />
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 14 }}>
              <label className="form-label">Email Address *</label>
              <input className="form-input" type="email" value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="judge@example.com" />
            </div>

            <div className="form-group" style={{ marginBottom: 14 }}>
              <label className="form-label">Affiliation</label>
              <input className="form-input" value={form.affiliation}
                onChange={e => setForm(f => ({ ...f, affiliation: e.target.value }))}
                placeholder="e.g. OAU Faculty of Technology, Siemens Nigeria" />
            </div>

            <div className="form-group" style={{ marginBottom: 14 }}>
              <label className="form-label">Domain Expertise</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input className="form-input" value={expertiseInput}
                  onChange={e => setExpertiseInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addExpertise()}
                  placeholder="e.g. Power Systems, Machine Learning" />
                <button className="btn btn-ghost btn-sm" onClick={addExpertise} type="button">Add</button>
              </div>
              {expertise.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {expertise.map(ex => (
                    <span key={ex} style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      padding: '3px 10px', borderRadius: 999, fontSize: '0.75rem',
                      background: 'rgba(42,157,143,0.1)', border: '1px solid rgba(42,157,143,0.25)', color: 'var(--teal)',
                    }}>
                      {ex}
                      <button onClick={() => setExpertise(e => e.filter(x => x !== ex))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--teal)', padding: 0, display: 'flex' }}>
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group" style={{ marginBottom: 24 }}>
              <label className="form-label">Bio (optional)</label>
              <textarea className="form-textarea" rows={3} value={form.bio}
                onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
                placeholder="Brief professional background…" />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={loading} style={{ flex: 2 }}>
                {loading ? <Spinner size={16} /> : 'Create Judge & Send Invite'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
